import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getCashOuts, getChangerStatus, updateChangerFill } from '../services/cashOutService';
import { getDashboard as getMaintenanceDashboard } from '../services/maintenanceService';
import { getChemicalDashboard } from '../services/chemicalService';
import ScrollHint from '../components/ScrollHint';
import { getRevenue } from '../services/plService';
import { useLocationContext } from '../contexts/LocationContext';
import WeatherBar from '../components/WeatherBar';
import ClockInOut from '../components/ClockInOut';

const Dashboard = () => {
  const { currentLocation } = useLocationContext();
  const [recentCashOuts, setRecentCashOuts] = useState([]);
  const [maintenance, setMaintenance] = useState(null);
  const [chemAlerts, setChemAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [plData, setPlData] = useState(null);

  const today = new Date().toISOString().split('T')[0];
  const thirtyDaysAgo = new Date(Date.now() - 30 * 86400000).toISOString().split('T')[0];
  const [plStart, setPlStart] = useState(thirtyDaysAgo);
  const [plEnd, setPlEnd] = useState(today);

  useEffect(() => {
    fetchData();
  }, [currentLocation?.id]);

  useEffect(() => {
    fetchPL();
  }, [plStart, plEnd, currentLocation?.id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [cashOutsData, maintenanceData, chemData] = await Promise.allSettled([
        getCashOuts({ limit: 5 }),
        getMaintenanceDashboard(),
        getChemicalDashboard()
      ]);

      if (cashOutsData.status === 'fulfilled') setRecentCashOuts(cashOutsData.value.cash_outs);
      if (maintenanceData.status === 'fulfilled') setMaintenance(maintenanceData.value);
      if (chemData.status === 'fulfilled') {
        const allStats = [...(chemData.value.ss_chemicals || []), ...(chemData.value.auto_chemicals || [])];
        const lowStock = allStats.filter(s => s.days_until_empty !== null && s.days_until_empty <= 30);
        lowStock.sort((a, b) => a.days_until_empty - b.days_until_empty);
        setChemAlerts(lowStock);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPL = async () => {
    try {
      const data = await getRevenue({ start_date: plStart, end_date: plEnd });
      setPlData(data);
    } catch (err) {
      console.error('Error fetching P&L data:', err);
    }
  };

  const fmt = (val) => {
    if (val == null) return '$0.00';
    return '$' + Number(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const calculateDailyAverage = (cashOut, index) => {
    if (index === recentCashOuts.length - 1) return 'N/A';
    const previousCashOut = recentCashOuts[index + 1];
    const currentDate = new Date(cashOut.date + 'T12:00:00');
    const previousDate = new Date(previousCashOut.date + 'T12:00:00');
    const daysDiff = Math.round((currentDate - previousDate) / (1000 * 60 * 60 * 24));
    if (daysDiff === 0) return '$' + cashOut.total_revenue.toFixed(2);
    return '$' + (cashOut.total_revenue / daysDiff).toFixed(2);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr + 'T12:00:00').toLocaleDateString();
  };

  const priorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'normal': return 'bg-blue-100 text-blue-800';
      case 'low': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  const hasOverdue = maintenance && (
    (maintenance.overdue_schedules?.length > 0) || (maintenance.overdue_work_orders?.length > 0)
  );

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
      </div>

      <ClockInOut />

      <WeatherBar />

      {/* ── Alerts Banner ─────────────────────────────────────────── */}
      {(hasOverdue || chemAlerts.length > 0) && (
        <div className="space-y-3">
          {hasOverdue && (
            <div className="bg-red-50 border border-red-300 rounded-lg p-4">
              <h2 className="text-sm font-semibold text-red-800 mb-1">Overdue Maintenance</h2>
              <div className="space-y-1">
                {maintenance.overdue_schedules?.map(s => (
                  <div key={`s-${s.id}`} className="text-sm text-red-700">
                    <span className="font-medium">{s.title}</span>
                    {s.equipment_category?.length > 0 && <span className="text-red-500 ml-1">({s.equipment_category.join(', ')})</span>}
                    {' '}&mdash; {Math.abs(s.days_until_due)} days overdue
                  </div>
                ))}
                {maintenance.overdue_work_orders?.map(wo => (
                  <div key={`wo-${wo.id}`} className="text-sm text-red-700">
                    <span className="font-medium">{wo.title}</span>
                    {' '}&mdash; due {formatDate(wo.due_date)}
                  </div>
                ))}
              </div>
              <Link to="/maintenance" className="text-red-800 hover:text-red-900 text-xs font-medium underline mt-1 inline-block">
                View maintenance
              </Link>
            </div>
          )}
          {chemAlerts.length > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h2 className="text-sm font-semibold text-yellow-800 mb-1">Low Stock Chemicals</h2>
              <div className="space-y-1">
                {chemAlerts.map((stats, i) => (
                  <div key={i} className="text-sm text-yellow-700">
                    <span className="font-medium">{stats.chemical.name}</span>
                    <span className="text-yellow-500 ml-1">({stats.chemical.location})</span>
                    {' '}&mdash; ~{Math.max(Math.round(stats.days_until_empty), 0)} days of supply remaining
                  </div>
                ))}
              </div>
              <Link to="/inventory" className="text-yellow-800 hover:text-yellow-900 text-xs font-medium underline mt-1 inline-block">
                View inventory
              </Link>
            </div>
          )}
        </div>
      )}

      {/* ── P&L Summary + Revenue Cards (side by side) ──────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* P&L Summary Table (left, 2/3 width) */}
        <div className="lg:col-span-2 bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <h2 className="text-lg font-semibold text-gray-900">P&L Summary</h2>
                <Link to="/pl" className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                  Full Report
                </Link>
              </div>
              <div className="flex items-center space-x-3">
                <input
                  type="date"
                  value={plStart}
                  onChange={(e) => setPlStart(e.target.value)}
                  className="border border-gray-300 rounded-md px-3 py-1.5 text-sm"
                />
                <span className="text-gray-500 text-sm">to</span>
                <input
                  type="date"
                  value={plEnd}
                  onChange={(e) => setPlEnd(e.target.value)}
                  className="border border-gray-300 rounded-md px-3 py-1.5 text-sm"
                />
              </div>
            </div>
          </div>
          {plData ? (
            <ScrollHint>
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Item</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-3 text-sm text-gray-900 pl-10">Credit Card Revenue</td>
                    <td className="px-6 py-3 text-sm text-right text-gray-900">{fmt(plData.cc_revenue?.total)}</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-3 text-sm text-gray-900 pl-10">Cash Revenue</td>
                    <td className="px-6 py-3 text-sm text-right text-gray-900">{fmt(plData.cash_revenue?.total)}</td>
                  </tr>
                  <tr className="bg-gray-50/50">
                    <td className="px-6 py-3 text-sm font-medium text-gray-900">Total Revenue</td>
                    <td className="px-6 py-3 text-sm text-right font-medium text-gray-900">
                      {fmt((plData.cc_revenue?.total || 0) + (plData.cash_revenue?.total || 0))}
                    </td>
                  </tr>
                  {plData.sales_tax > 0 && (
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-3 text-sm text-red-700 pl-10">Sales Tax ({plData.tax_rate}%)</td>
                      <td className="px-6 py-3 text-sm text-right text-red-700">({fmt(plData.sales_tax)})</td>
                    </tr>
                  )}
                  <tr className="bg-gray-50/50">
                    <td className="px-6 py-3 text-sm font-bold text-gray-900">Net Revenue</td>
                    <td className="px-6 py-3 text-sm text-right font-bold text-gray-900">{fmt(plData.net_revenue)}</td>
                  </tr>
                  {plData.expenses?.maintenance > 0 && (
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-3 text-sm text-red-700 pl-10">Maintenance</td>
                      <td className="px-6 py-3 text-sm text-right text-red-700">({fmt(plData.expenses.maintenance)})</td>
                    </tr>
                  )}
                  {Object.entries(plData.expenses?.utilities_by_category || {}).map(([cat, amount]) => (
                    <tr key={cat} className="hover:bg-gray-50">
                      <td className="px-6 py-3 text-sm text-red-700 pl-10 capitalize">{cat.replace(/_/g, ' ')}</td>
                      <td className="px-6 py-3 text-sm text-right text-red-700">({fmt(amount)})</td>
                    </tr>
                  ))}
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-3 text-sm text-red-700 pl-10">Soap/Chemicals (est.)</td>
                    <td className="px-6 py-3 text-sm text-right text-red-700">({fmt(plData.expenses?.soap)})</td>
                  </tr>
                  {plData.expenses?.total > 0 && (
                    <tr className="bg-gray-50/50">
                      <td className="px-6 py-3 text-sm font-medium text-red-700">Total Expenses</td>
                      <td className="px-6 py-3 text-sm text-right font-medium text-red-700">({fmt(plData.expenses.total)})</td>
                    </tr>
                  )}
                  <tr className="bg-gray-100 font-bold">
                    <td className="px-6 py-3 text-sm text-gray-900">Net Income</td>
                    <td className={`px-6 py-3 text-sm text-right ${plData.net >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                      {plData.net < 0 ? `(${fmt(Math.abs(plData.net))})` : fmt(plData.net)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </ScrollHint>
          ) : (
            <div className="p-6 text-center text-gray-500">Loading P&L data...</div>
          )}
        </div>

        {/* Revenue Cards (right, 1/3 width, stacked vertically) */}
        <div className="flex flex-col gap-4">
          {(() => {
            const combined = plData?.combined_by_unit || {};
            const totalRev = (plData?.cc_revenue?.total || 0) + (plData?.cash_revenue?.total || 0);
            const ssTotal = Object.entries(combined)
              .filter(([k]) => k.startsWith('SS Bay'))
              .reduce((s, [, v]) => s + (v.total || 0), 0);
            const autoTotal = combined['Auto Wash']?.total || 0;
            const vacTotal = combined['Vacuums']?.total || 0;
            const vendTotal = combined['Vending']?.total || 0;
            const pct = (part) => totalRev > 0 ? ((part / totalRev) * 100).toFixed(1) : '0';
            const periodDays = plData
              ? Math.max(1, Math.round((new Date(plEnd) - new Date(plStart)) / 86400000))
              : 1;

            return (
              <>
                <div className="bg-white p-5 rounded-lg shadow">
                  <div className="text-sm font-medium text-gray-600">Total Revenue</div>
                  <div className="text-2xl font-bold text-gray-900 mt-1">{fmt(totalRev)}</div>
                  <div className="text-xs text-gray-500 mt-1">{fmt(totalRev / periodDays)}/day avg</div>
                </div>
                <div className="bg-white p-5 rounded-lg shadow">
                  <div className="text-sm font-medium text-gray-600">Self-Service Bays</div>
                  <div className="text-2xl font-bold text-blue-600 mt-1">{fmt(ssTotal)}</div>
                  <div className="text-xs text-gray-500 mt-1">{pct(ssTotal)}% of total</div>
                </div>
                <div className="bg-white p-5 rounded-lg shadow">
                  <div className="text-sm font-medium text-gray-600">Auto Wash</div>
                  <div className="text-2xl font-bold text-green-600 mt-1">{fmt(autoTotal)}</div>
                  <div className="text-xs text-gray-500 mt-1">{pct(autoTotal)}% of total</div>
                </div>
                <div className="bg-white p-5 rounded-lg shadow">
                  <div className="text-sm font-medium text-gray-600">Vacuums & Vending</div>
                  <div className="text-2xl font-bold text-purple-600 mt-1">{fmt(vacTotal + vendTotal)}</div>
                  <div className="text-xs text-gray-500 mt-1">{pct(vacTotal + vendTotal)}% of total</div>
                </div>
                <div className={`p-5 rounded-lg shadow ${(plData?.net || 0) >= 0 ? 'bg-green-50' : 'bg-red-50'}`}>
                  <div className="text-sm font-medium text-gray-600">Net Profit</div>
                  <div className={`text-2xl font-bold mt-1 ${(plData?.net || 0) >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                    {(plData?.net || 0) < 0 ? `(${fmt(Math.abs(plData.net))})` : fmt(plData?.net)}
                  </div>
                  {plData?.sales_tax > 0 && (
                    <div className="text-xs text-gray-500 mt-1">After {fmt(plData.sales_tax)} sales tax</div>
                  )}
                  <div className="text-xs text-gray-500 mt-1">
                    {(plData?.net_revenue || 0) > 0 ? ((plData?.net || 0) / (plData.net_revenue) * 100).toFixed(1) : '0'}% margin
                  </div>
                </div>
              </>
            );
          })()}
        </div>
      </div>

      {/* ── Two-Column: Maintenance + Chemicals ───────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* Upcoming Maintenance & Open Tasks */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-900">Maintenance</h2>
            <Link to="/maintenance" className="text-blue-600 hover:text-blue-800 text-sm font-medium">
              View All
            </Link>
          </div>
          <div className="divide-y divide-gray-100">
            {/* Upcoming Schedules */}
            {maintenance?.upcoming_schedules?.length > 0 && (
              <div className="px-6 py-3">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Upcoming Scheduled</h3>
                {maintenance.upcoming_schedules.slice(0, 4).map(s => (
                  <div key={s.id} className="flex justify-between items-center py-1.5">
                    <div className="text-sm text-gray-900 min-w-0">
                      <span className="font-medium">{s.title}</span>
                      {s.equipment_category?.length > 0 && (
                        <>
                          <span className="text-gray-400 ml-1 text-xs hidden sm:inline">({s.equipment_category.join(', ')})</span>
                          <div className="text-gray-400 text-xs sm:hidden">{s.equipment_category.join(', ')}</div>
                        </>
                      )}
                    </div>
                    <span className="text-xs text-gray-500 whitespace-nowrap ml-2">
                      {s.days_until_due}d &mdash; {formatDate(s.next_due_date)}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Open Work Orders */}
            {maintenance?.open_work_orders?.length > 0 ? (
              <div className="px-6 py-3">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Open Tasks & Repairs</h3>
                {maintenance.open_work_orders.slice(0, 5).map(wo => (
                  <div key={wo.id} className="flex justify-between items-center py-1.5">
                    <div className="text-sm text-gray-900 flex items-center gap-2 min-w-0">
                      <span className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${
                        wo.priority === 'urgent' ? 'bg-red-500' :
                        wo.priority === 'high' ? 'bg-orange-500' : 'bg-blue-400'
                      }`} />
                      <div className="min-w-0">
                        <Link to={`/maintenance/work-order/${wo.id}`} className="hover:text-blue-600">
                          {wo.title}
                        </Link>
                        {(wo.equipment_area?.name || wo.equipment_category?.length > 0) && (
                          <>
                            <span className="text-gray-400 text-xs hidden sm:inline ml-1">
                              {wo.equipment_area?.name || wo.equipment_category.join(', ')}
                            </span>
                            <div className="text-gray-400 text-xs sm:hidden">
                              {wo.equipment_area?.name || wo.equipment_category.join(', ')}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                    <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${priorityColor(wo.priority)}`}>
                      {wo.priority}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              !maintenance?.upcoming_schedules?.length && (
                <div className="px-6 py-6 text-center text-sm text-gray-400">
                  No open tasks or upcoming maintenance.
                </div>
              )
            )}
          </div>
        </div>

        {/* Chemical Status */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-900">Chemical Inventory</h2>
            <Link to="/inventory" className="text-blue-600 hover:text-blue-800 text-sm font-medium">
              View All
            </Link>
          </div>
          {chemAlerts.length > 0 ? (
            <div className="px-6 py-3">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Low Stock</h3>
              {chemAlerts.map((stats, i) => (
                <div key={i} className="flex justify-between items-center py-1.5">
                  <div className="text-sm text-gray-900">
                    <span className="font-medium">{stats.chemical.name}</span>
                    <span className="text-gray-400 ml-1 text-xs">({stats.chemical.location === 'SS' ? 'SS Bays' : 'Auto Wash'})</span>
                  </div>
                  <span className="text-xs font-medium text-yellow-600">
                    ~{Math.max(Math.round(stats.days_until_empty), 0)} days supply
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="px-6 py-6 text-center text-sm text-gray-400">
              All chemicals are well stocked.
            </div>
          )}
        </div>
      </div>

      {/* ── Changer Status Estimator ──────────────────────────────── */}
      <ChangerEstimator />

      {/* ── Recent Cash Outs ──────────────────────────────────────── */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-900">Recent Cash Outs</h2>
          <Link
            to="/cash-outs"
            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
          >
            View All
          </Link>
        </div>
        <ScrollHint>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Revenue</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Self-Service</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Auto Wash</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vacuums</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Daily Avg Rev</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {recentCashOuts.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                    No cash outs yet. <Link to="/cash-out/new" className="text-blue-600 hover:text-blue-800">Create your first one</Link>
                  </td>
                </tr>
              ) : (
                recentCashOuts.map((cashOut, index) => (
                  <tr key={cashOut.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {new Date(cashOut.date + 'T12:00:00').toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                      ${cashOut.total_revenue.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ${cashOut.ss_total.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ${cashOut.auto_total.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ${cashOut.vac_total.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {calculateDailyAverage(cashOut, index)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600">
                      <Link to={`/cash-out/${cashOut.id}`} className="hover:text-blue-800">
                        View
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </ScrollHint>
      </div>
    </div>
  );
};

const LOOKBACK_OPTIONS = [
  { value: '30', label: '30 days' },
  { value: '60', label: '60 days' },
  { value: '90', label: '90 days' },
  { value: '180', label: '180 days' },
  { value: '365', label: '1 year' },
  { value: 'all', label: 'All time' },
];

const ChangerEstimator = () => {
  const { currentLocation } = useLocationContext();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingChanger, setEditingChanger] = useState(null);
  const [editBuckets, setEditBuckets] = useState(0);
  const [editDate, setEditDate] = useState('');
  const [fillUnit, setFillUnit] = useState('buckets'); // 'buckets' or 'dollars'
  const [editDollars, setEditDollars] = useState('');
  const [saving, setSaving] = useState(false);
  const [lookback, setLookback] = useState('90');

  useEffect(() => {
    fetchStatus();
  }, [currentLocation?.id, lookback]);

  const fetchStatus = async () => {
    try {
      setLoading(true);
      const data = await getChangerStatus({ lookback_days: lookback });
      setStatus(data);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (changer) => {
    setEditingChanger(changer.changer_number);
    setEditBuckets(changer.buckets || 0);
    setEditDollars('');
    setFillUnit('buckets');
    setEditDate(changer.fill_date || new Date().toISOString().split('T')[0]);
  };

  const saveEdit = async () => {
    try {
      setSaving(true);
      const buckets = fillUnit === 'buckets' ? editBuckets : (parseFloat(editDollars) || 0) / 250;
      await updateChangerFill({
        changer_number: editingChanger,
        buckets,
        fill_date: editDate,
      });
      setEditingChanger(null);
      fetchStatus();
    } catch {
      // keep editing
    } finally {
      setSaving(false);
    }
  };

  const pctColor = (pct) => {
    if (pct === null) return 'text-gray-400';
    if (pct <= 15) return 'text-red-600';
    if (pct <= 35) return 'text-yellow-600';
    return 'text-green-600';
  };

  const barColor = (pct) => {
    if (pct <= 15) return 'bg-red-500';
    if (pct <= 35) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  if (loading) return null;

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Changer Quarter Levels</h2>
            <p className="text-sm text-gray-500">
              Estimated from CC revenue
              {(() => {
                const periods = Math.max(0, ...(status?.changers || []).map(c => c.periods || 0));
                return periods ? ` (${periods} cash out${periods !== 1 ? 's' : ''} in window)` : '';
              })()}
            </p>
          </div>
          <label className="flex items-center gap-2 text-xs text-gray-600">
            Lookback
            <select
              value={lookback}
              onChange={(e) => setLookback(e.target.value)}
              className="border border-gray-300 rounded px-2 py-1 text-sm"
            >
              {LOOKBACK_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </label>
        </div>
      </div>
      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {(status?.changers || []).map(changer => (
            <div key={changer.changer_number} className="border border-gray-200 rounded-lg p-4">
              <div className="flex justify-between items-center mb-3">
                <h3 className="font-medium text-gray-900">Changer #{changer.changer_number}</h3>
                <button
                  onClick={() => startEdit(changer)}
                  className="text-blue-600 hover:text-blue-800 text-xs font-medium"
                >
                  {changer.fill_date ? 'Update Fill' : 'Set Fill'}
                </button>
              </div>

              {editingChanger === changer.changer_number ? (
                <div className="space-y-3">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <label className="block text-xs text-gray-600">Amount loaded</label>
                      <select
                        value={fillUnit}
                        onChange={(e) => setFillUnit(e.target.value)}
                        className="text-xs border border-gray-300 rounded px-1 py-0.5"
                      >
                        <option value="buckets">Buckets</option>
                        <option value="dollars">Dollars</option>
                      </select>
                    </div>
                    {fillUnit === 'buckets' ? (
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          onClick={() => setEditBuckets(Math.max(0, editBuckets - 1))}
                          className="w-8 h-8 flex items-center justify-center rounded bg-gray-200 hover:bg-gray-300 text-gray-700 font-bold"
                        >
                          &minus;
                        </button>
                        <span className="text-lg font-medium w-6 text-center">{editBuckets}</span>
                        <button
                          type="button"
                          onClick={() => setEditBuckets(editBuckets + 1)}
                          className="w-8 h-8 flex items-center justify-center rounded bg-gray-200 hover:bg-gray-300 text-gray-700 font-bold"
                        >
                          +
                        </button>
                      </div>
                    ) : (
                      <input
                        type="text"
                        inputMode="numeric"
                        value={editDollars}
                        onChange={(e) => setEditDollars(e.target.value)}
                        className="border border-gray-300 rounded px-2 py-1 w-full text-sm"
                        placeholder="e.g. 500"
                      />
                    )}
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Fill date</label>
                    <input
                      type="date"
                      value={editDate}
                      onChange={(e) => setEditDate(e.target.value)}
                      className="border border-gray-300 rounded px-2 py-1 w-full text-sm"
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={saveEdit}
                      disabled={saving}
                      className="text-green-600 hover:text-green-800 text-sm font-medium disabled:opacity-50"
                    >
                      Save
                    </button>
                    <button
                      onClick={() => setEditingChanger(null)}
                      className="text-gray-500 hover:text-gray-700 text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : changer.fill_date ? (
                <div>
                  <div className="text-xs text-gray-500 mb-2">
                    {changer.buckets} bucket{changer.buckets !== 1 ? 's' : ''} loaded on {new Date(changer.fill_date + 'T12:00:00').toLocaleDateString()}
                  </div>
                  {changer.pct_remaining !== null ? (
                    <>
                      <div className="flex items-end gap-2 mb-2">
                        <span className={`text-2xl font-bold ${pctColor(changer.pct_remaining)}`}>
                          {Math.round(changer.pct_remaining)}%
                        </span>
                        <span className="text-sm text-gray-500 mb-0.5">remaining</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
                        <div
                          className={`h-3 rounded-full ${barColor(changer.pct_remaining)}`}
                          style={{ width: `${Math.min(100, Math.max(0, changer.pct_remaining))}%` }}
                        />
                      </div>
                      {changer.estimated_days_left !== null && (
                        <div className="text-xs text-gray-500">
                          ~{changer.estimated_days_left} days until empty at current rate
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-sm text-gray-400">Waiting for CC revenue data...</div>
                  )}
                </div>
              ) : (
                <div className="text-sm text-gray-400">
                  No fill recorded — click "Set Fill" above
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
